import React, { useCallback, useEffect, useState } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  fetchRateLimitDurum,
  rateLimitKapat,
  rateLimitAc,
  rateLimitSifirla,
} from '../services/rateLimitService';

const SURE_SECENEKLERI = [
  { dakika: 30, etiket: '30 dk' },
  { dakika: 60, etiket: '1 saat' },
  { dakika: 240, etiket: '4 saat' },
  { dakika: 480, etiket: '8 saat' },
  { dakika: 1440, etiket: '1 gün' },
];

const KAPSAM_BILGI = {
  genel: {
    baslik: 'Genel API limiti',
    aciklama: 'Panel ve mobil uygulamanın tüm API istekleri (IP başına).',
  },
  giris: {
    baslik: 'Giriş limiti',
    aciklama: 'Hatalı giriş denemeleri ve token yenileme (IP başına).',
  },
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';
  return error?.data?.mesaj || error?.message || fallback;
};

const formatTarih = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const kalanSure = (bitis) => {
  if (!bitis) return '';
  const ms = new Date(bitis).getTime() - Date.now();
  if (ms <= 0) return 'süre doldu';
  const dk = Math.ceil(ms / 60000);
  if (dk < 60) return `${dk} dk kaldı`;
  const sa = Math.floor(dk / 60);
  const kalanDk = dk % 60;
  return `${sa} sa ${kalanDk} dk kaldı`;
};

const formatPencere = (ms) => {
  const dk = Math.round(ms / 60000);
  return dk >= 60 ? `${Math.round(dk / 60)} saat` : `${dk} dk`;
};

const kartStil = {
  background: '#fff',
  border: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '24px',
};

const RateLimitPage = () => {
  const { confirm } = useConfirmDialog();
  const [veri, setVeri] = useState(null);
  const [loading, setLoading] = useState(true);
  const [islem, setIslem] = useState(null);
  const [error, setError] = useState('');
  const [bilgi, setBilgi] = useState('');
  const [ozelDakika, setOzelDakika] = useState({ genel: '', giris: '' });
  const [, setTik] = useState(0);

  const yukle = useCallback(async (sessiz = false) => {
    if (!sessiz) setLoading(true);
    try {
      const res = await fetchRateLimitDurum();
      if (res?.basarili) {
        setVeri(res.veri);
        setError('');
      } else {
        setError(res?.mesaj || 'Durum alınamadı');
      }
    } catch (e) {
      setError(getErrorMessage(e, 'Durum alınamadı'));
    } finally {
      if (!sessiz) setLoading(false);
    }
  }, []);

  useEffect(() => {
    yukle();
    const yenile = setInterval(() => yukle(true), 10000);
    const saat = setInterval(() => setTik((t) => t + 1), 30000);
    return () => {
      clearInterval(yenile);
      clearInterval(saat);
    };
  }, [yukle]);

  const calistir = async (anahtar, fn, basariMesaji) => {
    setIslem(anahtar);
    setError('');
    setBilgi('');
    try {
      const res = await fn();
      if (res?.basarili) {
        if (res.veri) setVeri(res.veri);
        setBilgi(basariMesaji);
        setTimeout(() => setBilgi(''), 4000);
      } else {
        setError(res?.mesaj || 'İşlem başarısız');
      }
    } catch (e) {
      setError(getErrorMessage(e, 'İşlem başarısız'));
    } finally {
      setIslem(null);
    }
  };

  const kapat = async (kapsam, dakika) => {
    const dk = Number(dakika);
    const maks = veri?.maksKapaliDakika || 10080;
    if (!Number.isFinite(dk) || dk < 1 || dk > maks) {
      setError(`Süre 1 ile ${maks} dakika arasında olmalı.`);
      return;
    }
    const ok = await confirm({
      title: `${KAPSAM_BILGI[kapsam].baslik} kapatılsın mı?`,
      message: `Limit ${dk} dakika boyunca uygulanmayacak, süre dolunca otomatik açılacak. Bu sürede kaba kuvvet denemelerine karşı koruma azalır.`,
      variant: 'danger',
      confirmLabel: 'Kapat',
    });
    if (!ok) return;
    await calistir(`kapat-${kapsam}`, () => rateLimitKapat(kapsam, dk), 'Limit süreli olarak kapatıldı.');
  };

  const ac = (kapsam) =>
    calistir(`ac-${kapsam}`, () => rateLimitAc(kapsam), 'Limit tekrar açıldı.');

  const anahtarSifirla = (limiter, anahtar) =>
    calistir(`sifirla-${limiter}-${anahtar}`, () => rateLimitSifirla({ limiter, anahtar }), `${anahtar} sıfırlandı.`);

  const limiterSifirla = async (limiter) => {
    const ok = await confirm({
      title: 'Tüm sayaçlar sıfırlansın mı?',
      message: limiter
        ? `"${limiter}" limiterındaki tüm sayaçlar sıfırlanacak.`
        : 'Tüm limiterlardaki bütün sayaçlar sıfırlanacak.',
      confirmLabel: 'Sıfırla',
    });
    if (!ok) return;
    await calistir(`sifirla-${limiter || 'hepsi'}`, () => rateLimitSifirla({ limiter }), 'Sayaçlar sıfırlandı.');
  };

  return (
    <div className="page-container rate-limit-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1>Rate Limit</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="secondary-btn" onClick={() => yukle()} disabled={loading}>
            <i className="fas fa-rotate-right" style={{ marginRight: 6 }} />
            Yenile
          </button>
          <button
            type="button"
            className="secondary-btn"
            onClick={() => limiterSifirla(null)}
            disabled={!!islem || loading}
          >
            <i className="fas fa-eraser" style={{ marginRight: 6 }} />
            Hepsini sıfırla
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}
      {bilgi && (
        <div style={{ padding: '12px 16px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: 8, marginBottom: 16 }}>
          {bilgi}
        </div>
      )}

      {loading && !veri ? (
        <div style={kartStil}>Yükleniyor...</div>
      ) : veri ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 8 }}>
            {Object.keys(KAPSAM_BILGI).map((kapsam) => {
              const d = veri.kapsamlar?.[kapsam] || {};
              return (
                <section key={kapsam} style={{ ...kartStil, borderColor: d.kapali ? '#f59e0b' : kartStil.border }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <h2 style={{ fontSize: 18, margin: 0 }}>{KAPSAM_BILGI[kapsam].baslik}</h2>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontSize: 13,
                        fontWeight: 600,
                        background: d.kapali ? '#fef3c7' : '#dcfce7',
                        color: d.kapali ? '#92400e' : '#166534',
                      }}
                    >
                      {d.kapali ? 'Kapalı' : 'Açık'}
                    </span>
                  </div>
                  <p style={{ color: 'var(--text-gray)', fontSize: 14, marginBottom: 14 }}>{KAPSAM_BILGI[kapsam].aciklama}</p>

                  {d.kapali ? (
                    <>
                      <p style={{ fontSize: 14, marginBottom: 12 }}>
                        <strong>{formatTarih(d.kapaliBitis)}</strong> tarihinde otomatik açılacak ({kalanSure(d.kapaliBitis)}).
                        <br />
                        <span style={{ color: 'var(--text-gray)' }}>Kapatan: {d.kapatan || '—'}</span>
                      </p>
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={() => ac(kapsam)}
                        disabled={!!islem}
                      >
                        {islem === `ac-${kapsam}` ? 'Açılıyor...' : 'Şimdi aç'}
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, color: 'var(--text-gray)', marginBottom: 8 }}>Şu süre için kapat:</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        {SURE_SECENEKLERI.map((s) => (
                          <button
                            key={s.dakika}
                            type="button"
                            className="secondary-btn"
                            onClick={() => kapat(kapsam, s.dakika)}
                            disabled={!!islem}
                          >
                            {s.etiket}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="number"
                          min="1"
                          max={veri.maksKapaliDakika}
                          placeholder="Özel süre (dk)"
                          value={ozelDakika[kapsam]}
                          onChange={(e) => setOzelDakika((p) => ({ ...p, [kapsam]: e.target.value }))}
                          style={{ width: 150, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
                        />
                        <button
                          type="button"
                          className="secondary-btn"
                          onClick={() => kapat(kapsam, ozelDakika[kapsam])}
                          disabled={!!islem || !ozelDakika[kapsam]}
                        >
                          Kapat
                        </button>
                      </div>
                    </>
                  )}
                </section>
              );
            })}
          </div>

          {(veri.limiterlar || []).map((l) => (
            <section key={l.ad} style={kartStil}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <div>
                  <h2 style={{ fontSize: 17, margin: 0 }}>{l.aciklama}</h2>
                  <div style={{ fontSize: 13, color: 'var(--text-gray)', marginTop: 4 }}>
                    Sınır: {formatPencere(l.windowMs)} içinde {l.max} istek · Takipteki IP: {l.toplamAnahtar} · Limite takılan:{' '}
                    <strong style={{ color: l.engelliSayisi ? '#b91c1c' : undefined }}>{l.engelliSayisi}</strong>
                    {veri.kapsamlar?.[l.kapsam]?.kapali ? ' · şu an kapalı' : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => limiterSifirla(l.ad)}
                  disabled={!!islem || l.toplamAnahtar === 0}
                >
                  Bu listeyi sıfırla
                </button>
              </div>

              {l.kayitlar.length === 0 ? (
                <p style={{ color: 'var(--text-gray)', fontSize: 14, margin: 0 }}>Kayıt yok.</p>
              ) : (
                <div className="table-container">
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th>IP</th>
                        <th>İstek</th>
                        <th>Durum</th>
                        <th>Sayaç sıfırlanma</th>
                        <th className="islemler-sutunu">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {l.kayitlar.map((k) => (
                        <tr key={k.anahtar}>
                          <td style={{ fontFamily: 'monospace' }}>{k.anahtar}</td>
                          <td>
                            {k.istek} / {l.max}
                          </td>
                          <td>
                            {k.engelli ? (
                              <span style={{ color: '#b91c1c', fontWeight: 600 }}>Limite takıldı</span>
                            ) : (
                              <span style={{ color: 'var(--text-gray)' }}>Normal</span>
                            )}
                          </td>
                          <td>{formatTarih(k.sifirlanma)}</td>
                          <td className="table-actions islemler-sutunu">
                            <button
                              type="button"
                              className="secondary-btn"
                              onClick={() => anahtarSifirla(l.ad, k.anahtar)}
                              disabled={!!islem}
                            >
                              {islem === `sifirla-${l.ad}-${k.anahtar}` ? '...' : 'Sıfırla'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          <p style={{ fontSize: 12, color: 'var(--text-gray)' }}>
            Sayaçlar sunucu belleğinde tutulur; servis yeniden başlarsa sıfırlanır. Kapatma durumu kalıcıdır ve süre dolunca kendiliğinden açılır.
          </p>
        </>
      ) : null}
    </div>
  );
};

export default RateLimitPage;
